// External modules
import React, { useEffect } from 'react';
import { ThemeProvider } from '@emotion/react';
import { BodyText } from '@paypalcorp/pp-react-text-weasley-v7';
import { HeadingText } from '@paypalcorp/pp-react-text-weasley-v7';

// Internal modules
import {
  CI,
  flushLogsAndRedirect,
  getContent,
  getGlobals,
  useEndAndStartCi,
  useMountLogging,
} from '@/lib';
import { getTheme } from '@/styles';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/Button';
import { StepComponent } from './StepComponent';
import { ParentContainer } from '@/components/Common';
import { logFPTIForRealMoneyGamingPage } from './fpti';
import { CenteredContainer, Container } from './StyledComponents';
import { HaveAnAccountLogIn } from '@/components/Billing/Components';

/*
  Since this function is pure js and
  it does not depend on the component's state
  we declare it outside the RealMoneyGamingPage to omit
  unnecessary re renders
 */
const onContinueButtonClick = () => {
  const country = getGlobals().locality.country;
  logFPTIForRealMoneyGamingPage.onContinueButtonClick();
  CI.endWith(CI.Event.RMG_PAGE_USER_INPUT, CI.Status.Ok);
  flushLogsAndRedirect({
    url: `${getGlobals().uri.base}/${country.toLowerCase()}/welcome/signup`,
  });
};

export const RealMoneyGamingPage = (): JSX.Element => {
  useEndAndStartCi({
    end: CI.Event.APP_ROUTING,
    endStatus: CI.Status.Ok,
    start: CI.Event.RMG_PAGE_USER_INPUT,
  });
  useMountLogging('RealMoneyGamingPage');

  useEffect(() => {
    logFPTIForRealMoneyGamingPage.onRMGLandingPageLoad();
  }, []);

  return (
    <Layout
      isContentFullWidth={true}
      pageName="gaming"
      showHeader={true}
      showFooter={true}
      showValueProps={false}
    >
      <ThemeProvider theme={getTheme('ppReactV7')}>
        <Container data-testid="rmg-landing-page">
          <CenteredContainer>
            <HeadingText size="sm">
              {getContent('realMoneyGaming.landingPage.newLandingPageTitle')}
            </HeadingText>
            <ParentContainer display="flex" margin="1.5rem 0">
              <BodyText>
                {getContent(
                  'realMoneyGaming.landingPage.newLandingPageInfoMessage',
                )}
              </BodyText>
            </ParentContainer>
            <StepComponent
              content={getContent(
                'realMoneyGaming.landingPage.newLandingPageStepOneLabel',
              )}
              iconName="STEP_ONE_SPOT"
              margin="8px 0 24px 0"
            />
            <StepComponent
              content={getContent(
                'realMoneyGaming.landingPage.newLandingPageStepTwoLabel',
              )}
              iconName="STEP_TWO_SPOT"
              margin="0 0 2rem 0"
            />
            <Button onClick={onContinueButtonClick} type="button">
              {getContent('buttons.guest.base')}
            </Button>
            <HaveAnAccountLogIn />
          </CenteredContainer>
        </Container>
      </ThemeProvider>
    </Layout>
  );
};
